import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, BACKEND_URL, fmtDuration, fmtBytes } from "@/lib/api";
import { Film, Download, Trash2, Loader2, Sparkles, ArrowLeft, RotateCw, Link2, Pencil, Wand2, ScanSearch, Zap } from "lucide-react";
import { openInCapCut } from "@/lib/openInCapCut";
import RenameModal from "@/components/RenameModal";


async function copyReelDirectUrl(reelId) {
  try {
    const r = await api.get(`/reels/${reelId}/presigned-url`);
    const { url, thumbnail_url, filename } = r.data;
    const clipboardText = thumbnail_url
      ? `Video: ${url}\nThumbnail: ${thumbnail_url}`
      : url;
    try {
      await navigator.clipboard.writeText(clipboardText);
      toast.success(
        thumbnail_url ? "Video + thumbnail URLs copied · 7 days" : "Direct URL copied · 7 days",
        { description: filename },
      );
    } catch {
      window.prompt("Copy this URL (Ctrl/Cmd+C):", clipboardText);
    }
  } catch (err) {
    toast.error("Failed to generate URL: " + (err?.response?.data?.detail || err.message));
  }
}

export default function Reels() {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renaming, setRenaming] = useState(null); // reel being renamed
  const [autoBuilding, setAutoBuilding] = useState(false);
  const [blooperScanning, setBlooperScanning] = useState(false);
  const [blooperReeling, setBlooperReeling] = useState(false);

  const fetchReels = () => {
    api.get("/reels").then((r) => setReels(r.data)).finally(() => setLoading(false));
  };

  const autoBuildByEvent = async () => {
    if (!window.confirm(
      "Auto-build a 'Session Highlights' reel for every event that has 3+ tagged clips?\n\n" +
      "Existing reels with the same title are skipped."
    )) return;
    setAutoBuilding(true);
    try {
      const r = await api.post("/reels/auto-build-by-event", {
        min_clips: 3,
        ranking: "recent",
        add_brand: true,
        skip_existing: true,
      });
      const { queued = 0, skipped = 0 } = r.data || {};
      if (queued === 0 && skipped === 0) {
        toast.info("No events have enough clips yet — tag more clips with an event name and try again.");
      } else {
        toast.success(`Queued ${queued} reel${queued === 1 ? "" : "s"}${skipped ? ` \u00b7 skipped ${skipped}` : ""}`);
        fetchReels();
      }
    } catch (err) {
      toast.error("Auto-build failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setAutoBuilding(false);
    }
  };

  const startBlooperScan = async () => {
    if (!window.confirm(
      "Kick off a Bloopers Deep-Scan on every source video ≥ 60s long?\n\n" +
      "This fans out one Gemini job per video (up to 50 per batch) and " +
      "auto-skips anything already scanned in the last 7 days. Watch progress " +
      "on the Jobs page — once done, use 'Build Blooper Reel' below."
    )) return;
    setBlooperScanning(true);
    try {
      const r = await api.post("/blooper-batch/start", {
        min_duration: 60,
        max_videos: 50,
        window_seconds: 600,
        skip_recently_scanned: true,
      });
      const { queued = 0, skipped = 0 } = r.data || {};
      if (queued === 0 && skipped === 0) {
        toast.info("No videos matched — try lowering min duration.");
      } else {
        toast.success(`Blooper scan queued on ${queued} video${queued === 1 ? "" : "s"}${skipped ? ` \u00b7 skipped ${skipped}` : ""}`);
      }
    } catch (err) {
      toast.error("Blooper scan failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setBlooperScanning(false);
    }
  };

  const buildBlooperReel = async () => {
    if (!window.confirm("Compile every blooper clip in the Library into a single reel?")) return;
    setBlooperReeling(true);
    try {
      const r = await api.post("/blooper-batch/build-reel", {
        add_brand: true,
        max_clips: 60,
        within_days: 0,
      });
      toast.success(`Blooper reel queued \u00b7 ${r.data.clip_count} clips`);
      fetchReels();
    } catch (err) {
      toast.error("Build failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setBlooperReeling(false);
    }
  };

  useEffect(() => {
    fetchReels();
    const t = setInterval(() => {
      api.get("/reels").then((r) => {
        setReels(r.data);
        const stillRunning = r.data.some((rr) => rr.status === "queued" || rr.status === "running");
        if (!stillRunning) clearInterval(t);
      });
    }, 4000);
    return () => clearInterval(t);
  }, []);

  const handleDelete = async (reel) => {
    if (!window.confirm(`Delete "${reel.title}"?`)) return;
    await api.delete(`/reels/${reel.id}`);
    toast.success("Reel deleted");
    fetchReels();
  };

  const handleRetry = async (reel) => {
    await api.post(`/reels/${reel.id}/retry`);
    toast.success("Retrying...");
    fetchReels();
  };

  return (
    <div className="px-6 md:px-12 py-10 max-w-[1300px]" data-testid="reels-page">
      <div className="border-l-4 border-orange-600 pl-5 mb-8">
        <div className="label-tiny mb-2">Highlight Reels</div>
        <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Reels</h1>
        <p className="text-stone-400 mt-3">Multi-clip highlight packages — perfect for sales, sponsors, social.</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <Link to="/library" className="btn-secondary inline-flex items-center gap-1.5" data-testid="back-to-library">
            <ArrowLeft size={14} /> Back to Library to pick clips
          </Link>
          <button
            onClick={autoBuildByEvent}
            disabled={autoBuilding}
            className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-60"
            title="Group every event's clips into one auto-generated Session Highlights reel per event"
            data-testid="auto-build-by-event-btn"
          >
            {autoBuilding ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
            {autoBuilding ? "Queueing…" : "Auto-build by Event"}
          </button>
          <button
            onClick={startBlooperScan}
            disabled={blooperScanning}
            className="btn-secondary inline-flex items-center gap-1.5 hover:!border-amber-600 hover:!text-amber-400 disabled:opacity-60"
            title="Scan every video in the Library for wrecks, buck-offs and blooper-worthy moments (uses Gemini)"
            data-testid="blooper-batch-start-btn"
          >
            {blooperScanning ? <Loader2 size={14} className="animate-spin" /> : <ScanSearch size={14} />}
            {blooperScanning ? "Queueing…" : "Scan Library for Bloopers"}
          </button>
          <button
            onClick={buildBlooperReel}
            disabled={blooperReeling}
            className="btn-secondary inline-flex items-center gap-1.5 hover:!border-amber-600 hover:!text-amber-400 disabled:opacity-60"
            title="Compile every clip tagged category=blooper into a single reel — run the scan first"
            data-testid="blooper-reel-build-btn"
          >
            {blooperReeling ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            {blooperReeling ? "Queueing…" : "Build Blooper Reel"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-stone-500 py-20 text-center">Loading…</div>
      ) : reels.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-16 text-center" data-testid="reels-empty">
          <Sparkles size={32} className="mx-auto text-stone-600 mb-3" />
          <p className="text-stone-400">No reels yet.</p>
          <p className="text-stone-500 text-sm mt-2">Go to the Library, click <b>Select</b>, pick 2+ clips, then <b>Build Reel</b>.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="reels-grid">
          {reels.map((r) => <ReelCard key={r.id} reel={r} onDelete={() => handleDelete(r)} onRetry={() => handleRetry(r)} onRename={() => setRenaming(r)} />)}
        </div>
      )}

      {renaming && (
        <RenameModal
          label="Rename Reel"
          currentName={renaming.title}
          subtitle="Only changes the reel's display title — clips inside are untouched."
          testIdPrefix="reel-rename"
          maxLen={200}
          onSave={async (newName) => {
            await api.put(`/reels/${renaming.id}/rename`, { new_name: newName });
            setReels((prev) => prev.map((x) => x.id === renaming.id ? { ...x, title: newName } : x));
            toast.success("Reel renamed");
          }}
          onClose={() => setRenaming(null)}
        />
      )}
    </div>
  );
}

function ReelCard({ reel, onDelete, onRetry, onRename }) {
  const isReady = reel.status === "done";
  const isFailed = reel.status === "failed";
  return (
    <div className="border border-stone-800 hover:border-orange-700 transition-colors" data-testid={`reel-card-${reel.id}`}>
      <div className="aspect-video bg-black relative">
        {isReady ? (
          <video src={reel.stream_url || `${BACKEND_URL}/api/reels/${reel.id}/stream`} className="w-full h-full" controls preload="metadata" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {isFailed ? (
              <div className="text-rose-400 text-sm px-4 text-center">{reel.error || "Failed"}</div>
            ) : (
              <div className="text-stone-400">
                <Loader2 size={32} className="mx-auto animate-spin mb-2 text-orange-500" />
                <div className="text-xs uppercase tracking-widest">{reel.status} · {reel.progress || 0}%</div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 group/name">
          <div className="font-display uppercase font-bold text-xl truncate flex-1" title={reel.title}>{reel.title}</div>
          {onRename && (
            <button
              type="button"
              onClick={onRename}
              className="text-stone-500 hover:text-orange-400 p-1 opacity-0 group-hover/name:opacity-100 focus:opacity-100 transition-opacity"
              title="Rename reel"
              data-testid={`reel-rename-${reel.id}`}
            >
              <Pencil size={13} />
            </button>
          )}
        </div>
        <div className="font-mono-tc text-xs text-stone-500 mt-1 flex gap-2">
          <span>{reel.clip_ids?.length || 0} clips</span>
          {isReady && (<><span>·</span><span>{fmtDuration(reel.duration)}</span><span>·</span><span>{fmtBytes(reel.size_bytes)}</span></>)}
        </div>
        <div className="flex gap-2 mt-3 pt-3 border-t border-stone-800">
          {isReady && (
            <a href={`${BACKEND_URL}/api/reels/${reel.id}/download`} className="btn-secondary !py-1.5 flex-1 text-center inline-flex items-center justify-center gap-1.5" data-testid={`reel-download-${reel.id}`}>
              <Download size={12} /> Download
            </a>
          )}
          {isReady && (
            <button
              onClick={() => copyReelDirectUrl(reel.id)}
              className="btn-secondary !py-1.5 hover:!text-sky-400 inline-flex items-center justify-center gap-1.5"
              title="Copy direct S3 URL — paste into TikTok / Instagram / YouTube import"
              data-testid={`reel-copy-url-${reel.id}`}
            >
              <Link2 size={12} />
            </button>
          )}
          {isReady && (
            <button
              onClick={() => openInCapCut(reel.id, "reel")}
              className="btn-secondary !py-1.5 hover:!text-sky-400 inline-flex items-center justify-center gap-1.5"
              title="Save this reel and open CapCut so you can edit it there"
              data-testid={`reel-capcut-${reel.id}`}
            >
              <Film size={12} />
            </button>
          )}
          {isFailed && (
            <button onClick={onRetry} className="btn-primary !py-1.5 flex-1 inline-flex items-center justify-center gap-1.5" data-testid={`reel-retry-${reel.id}`}>
              <RotateCw size={12} /> Retry
            </button>
          )}
          <button onClick={onDelete} className="btn-secondary !py-1.5 hover:!text-rose-500" data-testid={`reel-delete-${reel.id}`}><Trash2 size={12} /></button>
        </div>
      </div>
    </div>
  );
}
